import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Loader, EmptyState, StatusBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Factory, ShieldCheck } from "lucide-react";

const TABS = ["VERIFICATION_PENDING", "VERIFIED", "REJECTED", "SUSPENDED", "All"];

export default function Manufacturers() {
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("VERIFICATION_PENDING");
  const [sel, setSel] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/manufacturers").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  if (!items) return <Loader />;

  const filtered = tab === "All" ? items : items.filter((m) => m.verification_status === tab);

  async function decide(action) {
    setBusy(true);
    try { await api.post(`/admin/manufacturers/${sel.id}/verify`, { action, reason });
      toast.success("Manufacturer updated"); setSel(null); setReason(""); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  }

  return (
    <div className="space-y-5">
      <h1 className="font-heading text-2xl font-semibold text-forest">Manufacturer Verification</h1>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap bg-card">{TABS.map((t) => <TabsTrigger key={t} value={t} data-testid={`mfgtab-${t.toLowerCase()}`} className="data-[state=active]:bg-forest data-[state=active]:text-white">{t.replace(/_/g, " ")}</TabsTrigger>)}</TabsList>
      </Tabs>

      {filtered.length === 0 ? <EmptyState title="Nothing here" hint="Manufacturers matching this status will appear here." icon={Factory} /> : (
        <div className="space-y-3">
          {filtered.map((m) => (
            <div key={m.id} data-testid={`admin-mfg-${m.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-5">
              <div>
                <p className="font-medium text-ink">{m.legal_name} <span className="text-ink/50">({m.trade_name})</span></p>
                <p className="num text-xs text-ink/50">GSTIN {m.gstin} · PAN {m.pan} · {m.state}</p>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={m.verification_status} />
                <Button data-testid={`review-mfg-${m.id}`} size="sm" className="bg-forest text-white hover:bg-forest/90" onClick={() => setSel(m)}>Review</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-forest" /> {sel?.trade_name}</DialogTitle></DialogHeader>
          {sel && (
            <div className="space-y-2 text-sm">
              {[["Legal name", sel.legal_name], ["GSTIN", sel.gstin], ["PAN", sel.pan], ["CIN", sel.cin || "—"],
                ["Address", `${sel.address}, ${sel.state} - ${sel.pincode}`], ["Contact", `${sel.contact_person} (${sel.designation})`],
                ["Email", sel.company_email], ["Mobile", sel.mobile], ["Categories", (sel.categories || []).join(", ")],
                ["Bank", sel.bank?.account_number ? `${sel.bank.account_number} · ${sel.bank.ifsc || ""}` : "Not provided"]].map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-2"><span className="text-ink/50">{k}</span><span className="col-span-2 text-ink">{v}</span></div>
              ))}
              <Textarea data-testid="verify-reason" className="mt-2" placeholder="Reviewer note / reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            <Button data-testid="verify-approve" className="bg-forest text-white hover:bg-forest/90" disabled={busy} onClick={() => decide("APPROVE")}>Approve</Button>
            <Button data-testid="verify-reqinfo" variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50" disabled={busy} onClick={() => decide("REQUEST_INFO")}>Request Info</Button>
            <Button data-testid="verify-reject" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" disabled={busy} onClick={() => decide("REJECT")}>Reject</Button>
            <Button data-testid="verify-suspend" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" disabled={busy} onClick={() => decide("SUSPEND")}>Suspend</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
